import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import type { EnrichmentSsvTsa } from '@uniticket/shared';
import { cn } from '@/lib/utils';
import type { SsvTsaState } from '@/hooks/useAddressValidation';

// La pastille SSV / TSA de la console DMP (N18), posée dans la fenêtre de validation à côté de la pastille
// Capacité (demande du user, 17-09). Mêmes règles que dans la console : VERTE dès qu'UN des trois documents
// existe, GRISE s'il n'y en a aucun, ABSENTE si la lecture a échoué — un échec n'a pas de réponse à donner, et
// une pastille grise se lirait « aucun document ». Le détail (quel document manque, les statuts Monday) est la
// même fiche que dans la console : `SsvTsaCard`, ouverte par la page.

/** Les documents d'une adresse : ceux de tous ses JVID, chaque item Monday compté une fois. La clé d'appariement
 *  est l'adresse sans le numéro de boîte : tous les logements d'une adresse remontent donc les mêmes items. */
export function ssvTsaDocsFor(jvids: number[], byJvid: Record<string, EnrichmentSsvTsa[]>): EnrichmentSsvTsa[] {
  const seen = new Set<string>();
  const out: EnrichmentSsvTsa[] = [];
  for (const jvid of jvids) {
    for (const doc of byJvid[String(jvid)] ?? []) {
      const k = JSON.stringify(doc);
      if (!seen.has(k)) { seen.add(k); out.push(doc); }
    }
  }
  return out;
}

export function SsvTsaPill({
  state, jvids, onOpen,
}: { state: SsvTsaState; jvids: number[]; onOpen: (docs: EnrichmentSsvTsa[]) => void }) {
  const { t } = useTranslation(['tickets']);
  if (jvids.length === 0) return null;

  // Pendant la lecture : le gris des « on ne sait pas encore », avec un sablier — comme la pastille Capacité.
  if (state.status === 'loading') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
        {t('tickets:dmpLookup.ssvTsa.pill')}
      </span>
    );
  }
  if (state.status !== 'ready' || !state.byJvid) return null;

  const docs = ssvTsaDocsFor(jvids, state.byJvid);
  const anyDoc = docs.some((d) => d.ssvDocLink || d.tsaDocForSignature || d.tsaDocSigned);
  const signedOn = docs.find((d) => d.tsaDocSigned)?.tsaSignedDate ?? null;

  return (
    <button
      type="button"
      // L'en-tête de groupe n'est pas cliquable, mais ses voisines le sont : même verrou que `CapacityPill`.
      onClick={(e) => { e.stopPropagation(); onOpen(docs); }}
      title={signedOn ? t('tickets:dmpLookup.ssvTsa.tsaSignedOn', { date: signedOn }) : t('tickets:dmpLookup.ssvTsa.hint')}
      aria-label={`${t('tickets:dmpLookup.ssvTsa.pill')} — ${t('tickets:dmpLookup.buildFile.openDetail')}`}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold transition-colors',
        anyDoc
          ? 'border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
          : 'border-slate-300 bg-slate-100 text-slate-500 hover:bg-slate-200',
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', anyDoc ? 'bg-emerald-500' : 'bg-slate-300')} aria-hidden="true" />
      {t('tickets:dmpLookup.ssvTsa.pill')}
    </button>
  );
}
